import { fetchCustomerAuthToken } from '@/api/login';
import { LENDER_DETAILS_TYPE } from '@/api/types';
import {
  CUSTOMER_TOKEN_FAILED_TIME,
  CUSTOMER_TOKEN_REFRESH_TIME,
  EMAIL_REGEX,
  INDIAN_CONTACT_COUNTRY_CODE,
  PIN_REGEX,
} from '@/constants';

/**
 * Util to verify email regex
 * @param {string} value
 * @returns {boolean}
 */
export const isValidEmail = (value: string): boolean => {
  return new RegExp(EMAIL_REGEX).test(value);
};

/**
 * Util to verify pin code against regex
 * @param {string} value
 * @returns {boolean}
 */
export const isValidPin = (value: string): boolean => {
  return PIN_REGEX.test(value);
};

let timeout: NodeJS.Timeout;
export const setCustomerAccessToken = (token = '', cb?: (header: string) => void): void => {
  try {
    if (token) {
      clearTimeout(timeout);
      // update Token
      timeout = setTimeout(async () => {
        const res = await fetchCustomerAuthToken(token);
        if (res.success && res.headers.xCustomerToken && cb) {
          // update the headers
          cb(res.headers.xCustomerToken as string);
        } else {
          // for safer side if refresh route failed deleting token from frontend at 25+4 min
          clearTimeout(timeout);
          timeout = setTimeout(() => {
            setCustomerAccessToken('');
          }, CUSTOMER_TOKEN_FAILED_TIME);
        }
      }, CUSTOMER_TOKEN_REFRESH_TIME);
    }
  } catch {
    // Catch token refresh Error
  }
};

export const isNoCostEmi = (interest: number): boolean => {
  return interest === 0;
};

export const getEmiInterestAmount = (payback: number, amount?: number): number => {
  if (amount) {
    const formattedAmountInRupees = amount / 100;
    return Math.ceil(formattedAmountInRupees / (1 - payback / 100) - formattedAmountInRupees);
  }
  return 0;
};

/**
 * sends a request to the specified url from a form. this will change the window location.
 * @param {string} path the path to send the post request to
 * @param {object} params the parameters to add to the url
 * @param {string} [method=POST] the method to use on the form
 */
export function formRequest(
  path: string,
  params: { request: string },
  method: 'GET' | 'POST' = 'POST',
): void {
  const form = document.createElement('form');
  form.method = method;
  form.action = path;

  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      const hiddenField = document.createElement('input');
      hiddenField.type = 'hidden';
      hiddenField.name = key;
      hiddenField.value = params[key as keyof typeof params];

      form.appendChild(hiddenField);
    }
  }

  document.body.appendChild(form);
  form.submit();
}

/**
 * A higher order function which takes a callback and returns a function
 * which calls the callback on pressing of the enter key
 *
 * @param callback The function to call on pressing the enter key
 * @returns A function which calls the callback on pressing the enter key
 */
export const onEnterKey =
  (callback: () => unknown) =>
  (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      callback();
    }
  };

/**
 * Format contact number with country code
 * @param {string} contact
 * @returns {string}
 */
export const formatContactNumber = (contact: string): string => {
  return !contact.includes(INDIAN_CONTACT_COUNTRY_CODE)
    ? `${INDIAN_CONTACT_COUNTRY_CODE}${contact}`
    : contact;
};

export const shouldListenForMessage = (
  event: MessageEvent<any>,
  window: Window & typeof globalThis,
): boolean => {
  return event.source === window.parent && window.parent !== window;
};

/**
 * Helper function to get the lender details matching the provider code
 */
export const getLenderDetailsFromTenure = ({
  provider,
  lenderDetails,
}: {
  provider: string;
  lenderDetails: LENDER_DETAILS_TYPE[];
}): LENDER_DETAILS_TYPE | null => {
  if (lenderDetails.length > 0) {
    return lenderDetails.find((lender) => lender.provider === provider) as LENDER_DETAILS_TYPE;
  }
  return null;
};

export function roundToTwo(num: number): number {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}
